import React, { useEffect, useState } from "react";
import { Activity, Loader2, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const METRICS = [
    { key: "pa_systolic", label: "PA Sistólica", unit: "mmHg", ref: { high: 140 } },
    { key: "pa_diastolic", label: "PA Diastólica", unit: "mmHg", ref: { high: 90 } },
    { key: "fc", label: "FC", unit: "bpm", ref: { low: 60, high: 100 } },
    { key: "fr", label: "FR", unit: "irpm", ref: { low: 12, high: 20 } },
    { key: "spo2", label: "SpO₂", unit: "%", ref: { low: 92 } },
    { key: "temp", label: "Temperatura", unit: "°C", ref: { high: 37.5 } },
    { key: "peso_kg", label: "Peso", unit: "kg" },
    { key: "imc", label: "IMC", unit: "kg/m²", ref: { low: 18.5, high: 24.9 } },
];

export default function VitalsFlowsheet({ patientId }) {
    const [data, setData] = useState({ series: [], latest: {} });
    const [loading, setLoading] = useState(true);
    const [chartMetric, setChartMetric] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/patients/${patientId}/vitals`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [patientId]);

    if (loading) {
        return (
            <div className="card-earthy flex items-center gap-2 text-sm text-muted-clinical">
                <Loader2 className="animate-spin" size={14} /> Carregando sinais vitais...
            </div>
        );
    }

    const { series, latest } = data;

    if (!series || series.length === 0) {
        return (
            <div className="card-earthy text-sm text-muted-clinical text-center py-8" data-testid="vitals-empty">
                <Activity size={28} className="mx-auto text-brand/40 mb-2" />
                <p>Nenhum sinal vital registrado ainda.</p>
                <p className="text-xs mt-1">Preencha o "Exame Físico" da AGA em uma consulta para começar a montar o flowsheet.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="vitals-flowsheet">
            <div className="card-earthy">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-lg text-brand flex items-center gap-2">
                        <Activity size={18} /> Sinais Vitais — Flowsheet
                    </h3>
                    <span className="text-xs text-muted-clinical">
                        {series.length} medição{series.length > 1 ? "ões" : ""}
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-[var(--border-organic)]">
                                <th className="py-2 pr-3 text-[11px] uppercase tracking-widest text-brand-secondary font-semibold sticky left-0 bg-[var(--bg-primary)]">
                                    Medida
                                </th>
                                {series.map((s) => (
                                    <th key={s.consultation_id} className="py-2 px-2 text-[11px] uppercase tracking-widest text-brand-secondary font-semibold whitespace-nowrap">
                                        {s.date || "—"}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {METRICS.map((m) => {
                                const hasSome = series.some((s) => s[m.key] != null);
                                if (!hasSome) return null;
                                return (
                                    <tr key={m.key} className="border-b border-[var(--border-organic)]/50 hover:bg-alt/40">
                                        <td className="py-1.5 pr-3 font-medium text-brand sticky left-0 bg-[var(--bg-primary)]">
                                            <button
                                                onClick={() => setChartMetric(m.key === chartMetric ? null : m.key)}
                                                className="text-left flex items-center gap-1 hover:underline"
                                                data-testid={`vital-metric-${m.key}`}
                                                title="Ver gráfico de tendência"
                                            >
                                                <TrendingUp size={11} /> {m.label}
                                                <span className="text-xs text-muted-clinical font-normal ml-1">({m.unit})</span>
                                            </button>
                                        </td>
                                        {series.map((s) => {
                                            const val = s[m.key];
                                            const highlight = flagFor(val, m.ref);
                                            return (
                                                <td
                                                    key={s.consultation_id}
                                                    className={`py-1.5 px-2 whitespace-nowrap ${highlight === "high" ? "text-red-700 font-semibold" : highlight === "low" ? "text-[#8A6A15] font-semibold" : "text-brand-secondary"}`}
                                                >
                                                    {val == null ? "—" : val}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-muted-clinical mt-2 italic">
                    Clique numa medida para ver o gráfico de tendência. Valores em vermelho estão acima da referência,
                    em ocre abaixo.
                </p>
            </div>

            {chartMetric && (
                <MetricChart series={series} metric={METRICS.find((m) => m.key === chartMetric)} onClose={() => setChartMetric(null)} />
            )}
        </div>
    );
}

function flagFor(val, ref) {
    if (val == null || !ref) return null;
    const n = typeof val === "number" ? val : parseFloat(String(val).replace(",", "."));
    if (Number.isNaN(n)) return null;
    if (ref.high != null && n > ref.high) return "high";
    if (ref.low != null && n < ref.low) return "low";
    return null;
}

function MetricChart({ series, metric, onClose }) {
    const points = series
        .filter((s) => s[metric.key] != null)
        .map((s) => ({ date: s.date, value: s[metric.key] }));
    return (
        <div className="card-earthy" data-testid={`vitals-chart-${metric.key}`}>
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-brand">
                    Tendência: {metric.label} ({metric.unit})
                </h4>
                <button onClick={onClose} className="btn-ghost text-xs">Fechar</button>
            </div>
            <div className="h-56">
                <ResponsiveContainer>
                    <LineChart data={points} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,63,51,0.1)" />
                        <XAxis dataKey="date" style={{ fontSize: 12 }} />
                        <YAxis style={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ background: "#FFF", border: "1px solid #ccc", borderRadius: 6 }} />
                        {metric.ref?.low != null && <ReferenceLine y={metric.ref.low} stroke="#8A6A15" strokeDasharray="4 4" label={{ value: `min ${metric.ref.low}`, position: "insideBottomLeft", fontSize: 10, fill: "#8A6A15" }} />}
                        {metric.ref?.high != null && <ReferenceLine y={metric.ref.high} stroke="#B33A3A" strokeDasharray="4 4" label={{ value: `max ${metric.ref.high}`, position: "insideTopLeft", fontSize: 10, fill: "#B33A3A" }} />}
                        <Line type="monotone" dataKey="value" stroke="#1E3F33" strokeWidth={2} dot={{ r: 4, fill: "#1E3F33" }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
